import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Row,
  Col,
  Badge,
  Table,
  Alert,
  Spinner,
  Modal,
  Form,
} from "react-bootstrap";
import { membershipAPI } from "../../api/membershipPlans";
import { gymMembersAPI } from "../../api/gymMembers";
import { subscriptionAPI } from "../../api/subscription";
import Swal from "sweetalert2";
import {
  FaArrowLeft,
  FaUser,
  FaCalendar,
  FaDollarSign,
  FaMoneyBillWave,
  FaPrint,
  FaEdit,
  FaTrash,
  FaFileInvoice,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { format } from "date-fns";

const PlanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [member, setMember] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [subscriptionPeriod, setSubscriptionPeriod] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    paid_amount: "",
    payment_mode: "cash",
    remarks: "",
  });

  useEffect(() => {
    if (id) {
      fetchPlanDetails();
    } else {
      navigate("/membership/plans");
    }
  }, [id, navigate]);

  const fetchPlanDetails = async () => {
    try {
      const [planResponse, paymentsResponse] = await Promise.all([
        membershipAPI.getPlanById(id),
        membershipAPI.getPlanPayments(id),
      ]);

      const planData = planResponse.data || planResponse;
      setPlan(planData);
      setPayments(paymentsResponse.data?.data || paymentsResponse.data || []);

      // Fetch related data
      await fetchRelatedData(planData);
    } catch (error) {
      console.error("Error fetching plan details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load plan details",
      });
      navigate("/membership/plans");
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedData = async (planData) => {
    try {
      const [memberResponse, typeResponse, periodResponse] = await Promise.all([
        gymMembersAPI.getMemberById(planData.member),
        subscriptionAPI.getSubscriptionTypeById(planData.subscription_type),
        subscriptionAPI.getSubscriptionPeriodById(planData.subscription_period),
      ]);

      setMember(memberResponse.data || memberResponse);
      setSubscriptionType(typeResponse.data || typeResponse);
      setSubscriptionPeriod(periodResponse.data || periodResponse);
    } catch (error) {
      console.error("Error fetching related data:", error);
    }
  };

  const handleAddPayment = () => {
    if (!plan) return;

    setPaymentForm({
      paid_amount: plan.pending_amount || "",
      payment_mode: "cash",
      remarks: "",
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!plan) return;

    const paymentAmount = parseFloat(paymentForm.paid_amount);
    const pendingAmount = parseFloat(plan.pending_amount);

    if (paymentAmount > pendingAmount) {
      Swal.fire({
        icon: "error",
        title: "Invalid Amount",
        text: `Payment amount cannot exceed pending amount (₹${pendingAmount})`,
      });
      return;
    }

    try {
      const paymentData = {
        plan: plan.id,
        paid_amount: paymentAmount,
        payment_mode: paymentForm.payment_mode,
        remarks: paymentForm.remarks,
      };

      await membershipAPI.createPayment(paymentData);

      Swal.fire({
        icon: "success",
        title: "Payment Added!",
        text: "Payment has been recorded successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      setShowPaymentModal(false);
      fetchPlanDetails(); // Refresh data
    } catch (error) {
      console.error("Error adding payment:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to record payment",
      });
    }
  };

  const handleDeletePlan = async () => {
    try {
      await membershipAPI.deletePlan(plan.id);

      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Membership plan has been deleted.",
        timer: 1500,
        showConfirmButton: false,
      });

      navigate("/membership/plans");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete plan",
      });
    } finally {
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd/MM/yyyy hh:mm a");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateRemainingDays = () => {
    if (!plan?.expiry_date) return null;
    const end = new Date(plan.expiry_date);
    const today = new Date();
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusBadge = () => {
    if (!plan) return null;

    if (!plan.is_active) {
      return <Badge bg="secondary">Inactive</Badge>;
    }

    const remainingDays = calculateRemainingDays();
    if (remainingDays <= 0) {
      return <Badge bg="danger">Expired</Badge>;
    } else if (remainingDays <= 7) {
      return <Badge bg="warning">Expiring Soon</Badge>;
    } else {
      return <Badge bg="success">Active</Badge>;
    }
  };

  const getFeeStatusBadge = () => {
    if (!plan) return null;

    switch (plan.fee_status?.toLowerCase()) {
      case "paid":
        return <Badge bg="success">Paid</Badge>;
      case "pending":
        return <Badge bg="warning">Pending</Badge>;
      case "partial":
        return <Badge bg="info">Partial</Badge>;
      default:
        return <Badge bg="secondary">Unknown</Badge>;
    }
  };

  const printPlanDetails = () => {
    const printContent = document.getElementById(
      "plan-details-content"
    ).innerHTML;
    const originalContent = document.body.innerHTML;

    document.body.innerHTML = printContent;
    window.print();
    document.body.innerHTML = originalContent;
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading plan details...</p>
      </div>
    );
  }

  if (!plan) {
    return (
      <Alert variant="danger">
        Plan not found.{" "}
        <Button variant="link" onClick={() => navigate("/membership/plans")}>
          Go back to plans
        </Button>
      </Alert>
    );
  }

  const remainingDays = calculateRemainingDays();

  return (
    <div className="plan-details-page">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <Button
              variant="outline-secondary"
              onClick={() => navigate("/membership/plans")}
              className="me-3"
            >
              <FaArrowLeft /> Back to Plans
            </Button>
            <h2 className="mb-0 d-inline">Plan Details</h2>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={printPlanDetails}>
              <FaPrint className="me-2" /> Print
            </Button>
            <Button
              variant="warning"
              onClick={() => navigate(`/membership/plans/edit/${plan.id}`)}
            >
              <FaEdit className="me-2" /> Edit Plan
            </Button>
            <Button variant="danger" onClick={() => setShowDeleteModal(true)}>
              <FaTrash className="me-2" /> Delete
            </Button>
          </div>
        </div>
        <p className="text-muted mb-0">Plan ID: {plan.id}</p>
      </div>

      <div id="plan-details-content">
        <Row>
          <Col lg={8}>
            {/* Plan Information Card */}
            <Card className="mb-4">
              <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaFileInvoice className="me-2" /> Plan Information
                </h5>
                {getStatusBadge()}
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <h6>Member Information</h6>
                      {member ? (
                        <div className="p-3 bg-light rounded">
                          <div className="d-flex align-items-center mb-2">
                            <FaUser className="me-2 text-primary" />
                            <strong>
                              {member.first_name} {member.last_name}
                            </strong>
                          </div>
                          <div className="mb-1">
                            <small className="text-muted">Member ID:</small>
                            <div>{member.member_id}</div>
                          </div>
                          <div className="mb-1">
                            <small className="text-muted">Mobile:</small>
                            <div>{member.mobile_number}</div>
                          </div>
                          <div className="mb-1">
                            <small className="text-muted">Email:</small>
                            <div>{member.email}</div>
                          </div>
                          <div>
                            <small className="text-muted">Address:</small>
                            <div>{member.address || "N/A"}</div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">Loading member details...</p>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <h6>Subscription Details</h6>
                      <div className="p-3 bg-light rounded">
                        <div className="mb-2">
                          <small className="text-muted">Type:</small>
                          <div className="fw-bold">
                            {subscriptionType?.name ||
                              `Type ${plan.subscription_type}`}
                          </div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Period:</small>
                          <div className="fw-bold">
                            {subscriptionPeriod
                              ? `${subscriptionPeriod.months} Month${
                                  subscriptionPeriod.months !== 1 ? "s" : ""
                                }`
                              : `${plan.subscription_period} Months`}
                          </div>
                        </div>
                        <div className="mb-2">
                          <small className="text-muted">Batch:</small>
                          <div className="fw-bold">
                            {plan.batch
                              ? plan.batch.charAt(0).toUpperCase() +
                                plan.batch.slice(1)
                              : "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <h6>Plan Dates</h6>
                      <div className="p-3 bg-light rounded">
                        <div className="mb-2 d-flex justify-content-between">
                          <span>Start Date:</span>
                          <span className="fw-bold">
                            <FaCalendar className="me-1" />
                            {formatDate(plan.plan_start_date)}
                          </span>
                        </div>
                        <div className="mb-2 d-flex justify-content-between">
                          <span>Expiry Date:</span>
                          <span className="fw-bold">
                            <FaCalendar className="me-1" />
                            {formatDate(plan.expiry_date)}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span>Days Remaining:</span>
                          <span
                            className={`fw-bold ${
                              remainingDays <= 7
                                ? "text-warning"
                                : "text-success"
                            }`}
                          >
                            <FaClock className="me-1" />
                            {remainingDays > 0
                              ? `${remainingDays} days`
                              : "Expired"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className="mb-3">
                      <h6>Payment Status</h6>
                      <div className="p-3 bg-light rounded">
                        <div className="mb-2 d-flex justify-content-between">
                          <span>Fee Status:</span>
                          <span>{getFeeStatusBadge()}</span>
                        </div>
                        <div className="mb-2 d-flex justify-content-between">
                          <span>Created On:</span>
                          <span>{formatDateTime(plan.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Payment History Card */}
            <Card className="mb-4">
              <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <FaMoneyBillWave className="me-2" /> Payment History
                </h5>
                <Button
                  variant="light"
                  size="sm"
                  onClick={handleAddPayment}
                  disabled={parseFloat(plan.pending_amount || 0) <= 0}
                >
                  <FaMoneyBillWave className="me-1" /> Add Payment
                </Button>
              </Card.Header>
              <Card.Body>
                {payments.length === 0 ? (
                  <div className="text-center py-4">
                    <FaMoneyBillWave size={48} className="text-muted mb-3" />
                    <h5>No payments recorded yet</h5>
                    <p className="text-muted">
                      Add the first payment for this plan
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table hover striped>
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Amount</th>
                          <th>Mode</th>
                          <th>Remarks</th>
                          <th>Recorded</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td>{formatDate(payment.payment_date)}</td>
                            <td className="fw-bold text-success">
                              {formatCurrency(payment.paid_amount)}
                            </td>
                            <td>
                              <Badge bg="info">
                                {payment.payment_mode?.toUpperCase()}
                              </Badge>
                            </td>
                            <td>{payment.remarks || "-"}</td>
                            <td>
                              <small className="text-muted">
                                {formatDateTime(payment.created_at)}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>
            {/* Financial Summary Card */}
            <Card className="mb-4">
              <Card.Header className="bg-warning text-dark">
                <h5 className="mb-0">
                  <FaDollarSign className="me-2" /> Financial Summary
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="financial-summary">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Registration Fee:</span>
                      <span className="fw-bold">
                        {formatCurrency(plan.registration_fee)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Plan Amount:</span>
                      <span className="fw-bold">
                        {formatCurrency(plan.amount)}
                      </span>
                    </div>
                    <hr />
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="fw-bold">Total Amount:</span>
                      <span className="fw-bold h5">
                        {formatCurrency(plan.total_amount || plan.amount)}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Paid Amount:</span>
                      <span className="fw-bold text-success">
                        {formatCurrency(plan.paid_amount)}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Pending Amount:</span>
                      <span className="fw-bold text-danger">
                        {formatCurrency(plan.pending_amount)}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Payment Progress</small>
                      <small>
                        {Math.round(
                          (parseFloat(plan.paid_amount) /
                            parseFloat(plan.total_amount || plan.amount)) *
                            100
                        )}
                        %
                      </small>
                    </div>
                    <div className="progress" style={{ height: "10px" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width: `${Math.min(
                            (parseFloat(plan.paid_amount) /
                              parseFloat(plan.total_amount || plan.amount)) *
                              100,
                            100
                          )}%`,
                        }}
                      ></div>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="mt-4">
                    <h6>Quick Stats</h6>
                    <Row className="mt-2">
                      <Col xs={6}>
                        <div className="text-center p-2 bg-light rounded">
                          <div className="text-success fw-bold">
                            {payments.length}
                          </div>
                          <small className="text-muted">Total Payments</small>
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-center p-2 bg-light rounded">
                          <div className="text-primary fw-bold">
                            {formatCurrency(
                              payments.reduce(
                                (sum, p) =>
                                  sum + parseFloat(p.paid_amount || 0),
                                0
                              )
                            )}
                          </div>
                          <small className="text-muted">Total Paid</small>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Plan Actions Card */}
            <Card>
              <Card.Header className="bg-info text-white">
                <h5 className="mb-0">Quick Actions</h5>
              </Card.Header>
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button
                    variant="success"
                    onClick={handleAddPayment}
                    disabled={parseFloat(plan.pending_amount || 0) <= 0}
                  >
                    <FaMoneyBillWave className="me-2" />
                    Add Payment
                  </Button>
                  <Button
                    variant="warning"
                    onClick={() =>
                      navigate(`/membership/plans/edit/${plan.id}`)
                    }
                  >
                    <FaEdit className="me-2" />
                    Edit Plan Details
                  </Button>
                  <Button
                    variant="outline-primary"
                    onClick={() => navigate(`/members/${member?.id}`)}
                    disabled={!member}
                  >
                    <FaUser className="me-2" />
                    View Member Profile
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={printPlanDetails}
                  >
                    <FaPrint className="me-2" />
                    Print Plan Details
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Add Payment Modal */}
      <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Payment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePaymentSubmit}>
          <Modal.Body>
            <div className="alert alert-info mb-3">
              <h6 className="alert-heading">Plan Details</h6>
              <p className="mb-1">
                <strong>Member:</strong> {member?.first_name}{" "}
                {member?.last_name}
              </p>
              <p className="mb-1">
                <strong>Total Amount:</strong>{" "}
                {formatCurrency(plan.total_amount || plan.amount)}
              </p>
              <p className="mb-1">
                <strong>Paid Amount:</strong> {formatCurrency(plan.paid_amount)}
              </p>
              <p className="mb-0">
                <strong>Pending Amount:</strong>{" "}
                {formatCurrency(plan.pending_amount)}
              </p>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Payment Amount (₹) *</Form.Label>
              <Form.Control
                type="number"
                value={paymentForm.paid_amount}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    paid_amount: e.target.value,
                  })
                }
                placeholder="Enter amount"
                min="0"
                max={plan.pending_amount}
                step="0.01"
                required
              />
              <Form.Text className="text-muted">
                Maximum: {formatCurrency(plan.pending_amount)}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Payment Mode *</Form.Label>
              <Form.Select
                value={paymentForm.payment_mode}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    payment_mode: e.target.value,
                  })
                }
                required
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="upi">UPI</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="cheque">Cheque</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Optional remarks about this payment..."
                value={paymentForm.remarks}
                onChange={(e) =>
                  setPaymentForm({
                    ...paymentForm,
                    remarks: e.target.value,
                  })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowPaymentModal(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Record Payment
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <FaTimesCircle className="me-2" />
            <strong>Warning:</strong> This action cannot be undone!
          </Alert>
          <p>
            Are you sure you want to delete the membership plan for{" "}
            <strong>
              {member?.first_name} {member?.last_name}
            </strong>
            ?
          </p>
          <p className="text-muted">
            This will permanently delete the plan and all associated payment
            records.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeletePlan}>
            Delete Plan
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlanDetails;
